import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useSessionStore } from '@stores/sessionStore';
import LoadingSpinner from '@components/LoadingSpinner';

interface DeviceInfo {
  userAgent: string;
  deviceName: string;
  screenSize: string;
  language: string;
  timezone: string;
  browserId: string;
}

/** Get or create a stable browser-profile ID stored in localStorage. */
function getBrowserId(): string {
  const key = 'gcxp_browser_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

interface ActivationResponse {
  sessionId: string;
  hotelSlug: string;
  hotelName: string;
  guestName?: string;
  roomNumber?: string;
  guestCount?: number;
  checkInAt?: string;
  checkOutAt?: string;
  expiresAt?: string;
  hotelTimeZone?: string;
  availableModules: string[];
  availableStations?: { id: number; name: string; iconEmoji?: string; color?: string }[];
  isNewDevice: boolean;
  deviceNumber: number;
  maxDevices: number;
  token: string;
  themeColors?: {
    background?: string;
    color1?: string;
    color2?: string;
    color3?: string;
    textColor?: string;
  };
  defaultLanguage?: string;
  trustLevel?: number;
  // Session scope fields for non-room sessions
  sessionType?: string;
  scope?: string;
  scopeValue?: string;
  displayLabel?: string;
}

/**
 * `room` — the room QR / portal link (session token). `reservation` — the pre-check-in link (signed
 * reservation token): same consent, same device registration, same portal, but resolved through the
 * reservation to its stay session, so a guest can come in before they have a room.
 */
export default function ActivatePage({ mode = 'room' }: { mode?: 'room' | 'reservation' } = {}) {
  const checkEndpoint = mode === 'reservation' ? '/api/v1/guest/check-reservation' : '/api/v1/guest/check-token';
  const activateEndpoint = mode === 'reservation' ? '/api/v1/guest/activate-reservation' : '/api/v1/guest/activate';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession, clearSession, validateSession } = useSessionStore();
  const [error, setError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [isCheckingExistingSession, setIsCheckingExistingSession] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  // Server's verdict on the session this browser already holds: keep it, or let the link replace it.
  const [keepCurrent, setKeepCurrent] = useState(false);
  const [consentToken, setConsentToken] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setError(mode === 'reservation'
        ? 'This link is incomplete. Please open the link from your message again.'
        : 'No token provided. Please scan the QR code again.');
      setIsCheckingToken(false);
      return;
    }

    // Check if token is valid before showing consent modal
    checkTokenValidity(token);
  }, [searchParams]);

  // When token is valid, check for existing session
  useEffect(() => {
    if (consentToken && !showConsent && !isCheckingExistingSession) {
      checkExistingSession();
    }
  }, [consentToken]);

  // Never ask the guest to choose. Re-opening their own link (WhatsApp's in-app browser does it on every
  // tap) just returns them to it; the link replaces the stored session only when the server says so — that
  // session has ended, or it's a pre-arrival session and this link is for the stay itself.
  const checkExistingSession = async () => {
    setIsCheckingExistingSession(true);

    await validateSession();
    const { isValid: hasValidSession, session: currentSession } = useSessionStore.getState();

    if (hasValidSession && currentSession && keepCurrent) {
      navigate(`/${currentSession.hotelSlug}/welcome`, { replace: true });
      return;
    }
    if (hasValidSession) {
      clearSession();
    }
    setShowConsent(true);
    setIsCheckingExistingSession(false);
  };

  const checkTokenValidity = async (token: string) => {
    try {
      const currentSessionId = localStorage.getItem('gcxp_session_id') || undefined;
      const { data } = await apiClient.post<{ keepCurrent?: boolean; KeepCurrent?: boolean }>(
        checkEndpoint, { token, currentSessionId });
      setKeepCurrent(Boolean(data?.keepCurrent ?? data?.KeepCurrent));
      // Token is valid, store it and check for existing session
      setConsentToken(token);
      setIsCheckingToken(false);
      // The useEffect will handle checking existing session and showing appropriate modal
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { Error?: string } } };
      const status = e.response?.status;
      // Only block on explicit token rejection (401) — if endpoint is unreachable (404/5xx),
      // proceed to activation which will catch invalid tokens itself.
      if (status === 401) {
        const errorMsg = e.response?.data?.Error || 'Invalid or expired token. Please request a new QR code.';
        setError(errorMsg);
        setIsCheckingToken(false);
      } else {
        // check-token endpoint unavailable — proceed anyway, activate will validate
        setConsentToken(token);
        setIsCheckingToken(false);
      }
    }
  };

  const handleAcceptConsent = () => {
    if (consentToken) {
      setShowConsent(false);
      setIsActivating(true);
      activateToken(consentToken);
    }
  };

  const getDeviceInfo = (): DeviceInfo => {
    const userAgent = navigator.userAgent;
    let deviceName = 'Unknown Device';

    // Detect device type
    if (/iPhone/.test(userAgent)) {
      deviceName = 'iPhone';
    } else if (/iPad/.test(userAgent)) {
      deviceName = 'iPad';
    } else if (/Android/.test(userAgent)) {
      if (/Mobile/.test(userAgent)) {
        deviceName = 'Android Phone';
      } else {
        deviceName = 'Android Tablet';
      }
    } else if (/Mac/.test(userAgent)) {
      deviceName = 'Mac';
    } else if (/Windows/.test(userAgent)) {
      deviceName = 'Windows PC';
    } else if (/Linux/.test(userAgent)) {
      deviceName = 'Linux PC';
    }

    // Add browser info
    if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
      deviceName += ' (Chrome)';
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      deviceName += ' (Safari)';
    } else if (/Firefox/.test(userAgent)) {
      deviceName += ' (Firefox)';
    } else if (/Edg/.test(userAgent)) {
      deviceName += ' (Edge)';
    }

    return {
      userAgent,
      deviceName,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      browserId: getBrowserId(),
    };
  };

  const activateToken = async (token: string) => {
    try {
      const deviceInfo = getDeviceInfo();
      // Pass the activation secret (v param) if present — enables auto-verify for SMS/Email links
      const v = searchParams.get('v') || undefined;

      const response = await apiClient.post<{ success: boolean; data: ActivationResponse; error?: string }>(
        activateEndpoint,
        {
          token,
          v,
          deviceInfo,
        }
      );

      if (response.status === 200 && response.data) {
        const data = response.data.data || response.data;
        
        // Clear any stale theme from previous hotel before applying new one
        localStorage.removeItem('gcxp_theme_colors');
        const root = document.documentElement;
        root.style.removeProperty('--theme-background');
        root.style.removeProperty('--theme-color-1');
        root.style.removeProperty('--theme-color-2');
        root.style.removeProperty('--theme-color-3');
        root.style.removeProperty('--theme-text-color');
        root.classList.remove('hotel-themed');
        
        // Store the session data in localStorage (persists until checkout)
        localStorage.setItem('gcxp_token', data.token);
        localStorage.setItem('gcxp_session_id', data.sessionId);
        localStorage.setItem('gcxp_hotel_slug', data.hotelSlug);
        if (data.expiresAt) {
          localStorage.setItem('gcxp_expires_at', data.expiresAt); // UTC expiry time
        }
        if (data.checkOutAt) {
          localStorage.setItem('gcxp_checkout_at', data.checkOutAt);
        }
        if (data.hotelTimeZone) {
          localStorage.setItem('gcxp_hotel_timezone', data.hotelTimeZone);
        }

        // Set session store state immediately so App.tsx doesn't show loading/expired
        setSession({
          sessionId: data.sessionId,
          hotelSlug: data.hotelSlug,
          hotelName: data.hotelName,
          expiresAt: data.expiresAt,
          availableModules: data.availableModules || [],
          availableStations: data.availableStations || [],
          themeColors: data.themeColors,
          language: data.defaultLanguage,
          trustLevel: data.trustLevel ?? 0,
          scope: data.scope,
          scopeValue: data.scopeValue,
        });

        // Redirect to welcome page with activation data in state
        // This ensures the welcome page has data immediately without needing to wait for API
        navigate(`/${data.hotelSlug}/welcome`, { 
          replace: true,
          state: { 
            justActivated: true,
            token: data.token,
            sessionId: data.sessionId,
            hotelSlug: data.hotelSlug,
            hotelName: data.hotelName,
            guestName: data.guestName,
            roomNumber: data.roomNumber,
            guestCount: data.guestCount,
            deviceNumber: data.deviceNumber,
            maxDevices: data.maxDevices,
            availableModules: data.availableModules,
            availableStations: data.availableStations,
            checkInAt: data.checkInAt,
            checkOutAt: data.checkOutAt,
            sessionType: data.sessionType,
            displayLabel: data.displayLabel,
            scope: data.scope,
            scopeValue: data.scopeValue,
          }
        });
      } else {
        setError(response.data?.error || 'Failed to activate. Please try again.');
        setIsActivating(false);
      }
    } catch (err: unknown) {
      console.error('Activation error:', err);
      const e = err as { response?: { status?: number; data?: { MaxDevices?: number; Error?: string } } };

      // Handle specific error cases
      if (e.response?.status === 429) {
        const data = e.response?.data;
        setError(
          `Device limit reached. This check-in allows ${data?.MaxDevices || 'limited'} device(s). ` +
          `All devices have been registered. Please contact the front desk if you need assistance.`
        );
      } else if (e.response?.status === 401) {
        setError(e.response?.data?.Error || 'Invalid or expired token. Please request a new QR code.');
      } else {
        setError('Unable to connect. Please check your internet connection and try again.');
      }
      setIsActivating(false);
    }
  };

  // Show loading while checking token validity
  if (isCheckingToken) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light">
        <LoadingSpinner />
        <p className="mt-3 text-muted">Verifying access link...</p>
      </div>
    );
  }

  // Show loading while checking for existing session
  if (isCheckingExistingSession || (consentToken && !showConsent && !error)) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light">
        <LoadingSpinner />
        <p className="mt-3 text-muted">Checking session...</p>
      </div>
    );
  }

  // Show consent modal
  if (showConsent) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light p-4">
        <div className="card shadow-sm" style={{ maxWidth: '480px', width: '100%' }}>
          <div className="card-body text-center p-4">
            <div className="mb-3">
              <span style={{ fontSize: '3rem' }}>🏨</span>
            </div>
            <h2 className="h4 mb-3">Welcome to Golden Crown Hotels</h2>
            <p className="text-muted mb-4">
              Before accessing our guest services, please review and accept our terms.
            </p>
            
            <div className="text-start bg-light rounded p-3 mb-4" style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
              <p className="mb-2">
                <strong>By continuing, you agree to:</strong>
              </p>
              <ul className="mb-0">
                <li className="mb-1">
                  Our <a href="https://guest.goldencrown-hotels.com/legal/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> which govern your use of the guest platform
                </li>
                <li className="mb-1">
                  Our <a href="https://guest.goldencrown-hotels.com/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> explaining how we collect and use your data
                </li>
                <li>
                  The collection of device information to provide you with personalized services during your stay
                </li>
              </ul>
              <p className="mt-3 mb-0 text-muted" style={{ fontSize: '0.8rem' }}>
                You can request deletion of your data at any time via our <a href="https://guest.goldencrown-hotels.com/legal/data-deletion" target="_blank" rel="noopener noreferrer">Data Deletion</a> page.
              </p>
            </div>

            <div className="d-grid">
              <button
                className="btn btn-primary btn-lg"
                onClick={handleAcceptConsent}
              >
                Accept &amp; Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isActivating) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light">
        <LoadingSpinner />
        <p className="mt-3 text-muted">Activating your session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light p-4">
        <div className="text-center">
          <div className="mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              fill="currentColor"
              className="text-danger"
              viewBox="0 0 16 16"
            >
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
            </svg>
          </div>
          <h1 className="h4 mb-3">Activation Failed</h1>
          <p className="text-muted mb-4">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null;
}
